import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { useAuth } from '@/hooks/use-auth';
import { campaignService } from '@/services/data/CampaignService';
import type { CampaignWithCoverage } from '@/services/data/CampaignService';

export function useActivePlan(isGenerating = false, planId?: string | null) {
  const { session } = useAuth();
  const userId = session?.user?.id;

  const {
    data: activePlan,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['activePlan', userId, planId || 'latest'],
    queryFn: async (): Promise<CampaignWithCoverage | null> => {
      if (!userId) return null;
      if (planId) {
        const result = await campaignService.getCampaignWithCoverage(planId);
        return result.data ?? null;
      }
      const result = await campaignService.getActivePlan(userId);
      return result.data ?? null;
    },
    enabled: !!userId,
    staleTime: isGenerating ? 2_000 : 30_000,
    refetchInterval: isGenerating ? 3_000 : false,
    refetchOnWindowFocus: true,
    // Same bug as the workflow panel's now-fixed `panel-workflow-project`
    // query (2026-08-19): no placeholderData means every one of the 3s
    // polling refetches above — the MOST exposed instance of this pattern in
    // the app, since it fires on a timer during every active generation, not
    // just on window refocus — drops `activePlan` to `undefined` for one
    // render. `computeShowWorkflowKickoff` (CreateChatPage.tsx) reads that as
    // "workflow not started," flipping the full kickoff skeleton back on
    // mid-conversation with nothing actually wrong.
    placeholderData: keepPreviousData,
  });

  return { activePlan: activePlan ?? null, isLoading, isError, refetch };
}
